import { FunctionComponent, SetStateAction, useEffect, useMemo, useState } from 'react';
import styles from './TrainingComponent.module.scss';
import { DataVaraible, SessionAddressOverride, Training, versionPetAndServices } from 'model/NewMultipleBooking';
import { useApiClientWrapper, useMarketplace, usePermissions, useTimeZone } from 'hooks';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'store';
import { ServicesStatus } from 'reducers/services/ServicesState';
import { NewBookingsStatus } from '@spike/new-bookings-action';
import { OrdersStatus } from '@spike/orders-action';
import { StaffStatus } from 'reducers/staff/StaffState';
import Button from 'components/UI/V3/Button/Button';
import StaffSelector from '../StaffSelector/StaffSelector';
import TrainingServiceSelector from './UI/TrainingServiceSelector/TrainingServiceSelector';
import { Input } from 'components/UI/V3/Forms/Input/Input';
import { ValueTypeSelector } from 'components/UI/V3/Forms/ValueTypeSelector';
import DurationSelect from '../ServiceSelector/DurationSelect';
import PriceField from 'components/UI/V3/Forms/PriceField';
import { SERVICES_BUSINESS_AREAS, TrainingService } from 'model/Service';
import { ClientsStatus, getClientThunk } from '@spike/clients-action';
import useNonInitialEffect from '@versiondos/hooks';
import Client from '@spike/client-model';
import Spinner from 'components/UI/V3/Spinner';
import { fetchStaffThunk } from 'actions/staff/StaffActions';
import Staff from 'model/Staff';
import { RecentlyBooked } from '../UI/RecentlyBooked';
import { AppointmentHistoryDrawer } from '../UI/AppointmentHistoryDrawer';
import OrderDetail from 'components/OrderDetail';
import { AppointmentListItem } from '@spike/appointments-list-model';
import clsx from 'clsx';
import lockStyles from '../UI/priceFieldInteractionLock.module.scss';
import type { Enrollment, MembershipItem } from '@spike/membership-model';
import type { EnrollmentAnchor } from '../utils/enrollmentAnchor';
import { PetCustomPrice } from '@spike/pet-model';
import { fetchPetCustomPricesThunk } from '@spike/pets-action';
import { resolveStandardPrice } from '../utils/bookingCustomPrices';
import SaveCustomPriceCheckbox from '../UI/SaveCustomPriceCheckbox';
import SavedCustomPriceChoice from '../UI/SavedCustomPriceChoice';
import { useSavedCustomPriceChoice } from '../UI/useSavedCustomPriceChoice';
import MultipleDateSelector from '../DateSelector/MultipleDateSelector';
import PeriodSelector from '../DateSelector/PeriodSelector';
import BookingAlertList from '../UI/BookingAlertList';
import SessionFullAlert from '../UI/SessionFullAlert';
import SessionSelector from '../UI/SessionSelector/SessionSelector';
import ClientAddressAutocomplete from '../UI/ClientAddressAutocomplete/ClientAddressAutocomplete';
import useBookingAlerts from '../UI/useBookingAlerts';
import { mergeRoutedAlerts, splitSessionAlerts } from '../UI/bookingAlerts';
import useSessionAlerts from '../UI/useSessionAlerts';
import useSessionPicker from '../UI/useSessionPicker';
import useClientAddressPrefill from '../UI/useClientAddressPrefill';
import { SelectedPlace } from 'hooks/usePlacesAutocomplete';
import { WalkingSession } from '@spike/new-booking-model';
import { noop } from 'lodash';
import moment, { Moment } from 'moment-timezone';

interface TrainingProps {
    clientId: number;
    petId: number;
    training: Training;
    appointmentId?: number;
    servicesUsed?: Array<number>;
    inHomeService?: boolean;
    enrollment?: Enrollment;
    membershipItem?: MembershipItem;
    enrollmentAnchor?: EnrollmentAnchor;
    versionPetAndServices?: versionPetAndServices;
    draftSessionBookings?: Array<DataVaraible>;
    onNext: (training: Training) => void;
}

export const TrainingComponent: FunctionComponent<TrainingProps> = ({
    clientId,
    petId,
    training,
    appointmentId,
    servicesUsed,
    inHomeService,
    enrollment,
    membershipItem,
    versionPetAndServices: versionMode,
    draftSessionBookings = [],
    onNext
}) => {
    const permissions = usePermissions();
    const canEditPrices = permissions.checkout.editPrices;
    const canApplyDiscount = permissions.checkout.applyDiscounts;
    const dispatch = useDispatch();
    const apiClientWrapper = useApiClientWrapper();
    const marketplace = useMarketplace();
    const timeZone = useTimeZone();
    const tz = marketplace.schedule.timeZone;
    const newBookingStatus = useSelector<RootState, NewBookingsStatus>(state => state.newBookings.status);
    const ordersStatus = useSelector<RootState, OrdersStatus>(state => state.orders.status);
    const servicesStatus = useSelector<RootState, ServicesStatus>(state => state.services.status);
    const isEdit = versionMode === versionPetAndServices.edit;

    const normalizeTrainingDates = (trainingData: Training): Training => {
        if (!trainingData) return trainingData;
        return {
            ...trainingData,
            from:
                trainingData.from && trainingData.from.isValid()
                    ? trainingData.from.clone().tz(timeZone)
                    : trainingData.from,
            to: trainingData.to && trainingData.to.isValid() ? trainingData.to.clone().tz(timeZone) : trainingData.to,
            selectedDates: trainingData.selectedDates?.map(d => d.clone().tz(tz).startOf('day'))
        };
    };

    const [trainingState, setTraining] = useState<Training>(() => normalizeTrainingDates(training));
    const [selectedDates, setSelectedDates] = useState<Array<Moment>>(
        training?.selectedDates?.length
            ? training.selectedDates.map(d => d.clone().tz(tz).startOf('day'))
            : training?.from
            ? [training.from.clone().tz(tz).startOf('day')]
            : []
    );
    const clientsStatus = useSelector<RootState, ClientsStatus>(state => state.clients.status);
    const client = useSelector<RootState, Client | undefined>(state => state.clients.client);
    const staffs = useSelector<RootState, Array<Staff>>(state => state.staff.staff);
    const staffStatus = useSelector<RootState, StaffStatus>(state => state.staff.status);
    const customPrices = useSelector<RootState, Array<PetCustomPrice>>(state => state.pets.customPrices);
    const [inHomeServiceState, setInHomeService] = useState<boolean | undefined>(inHomeService);
    const [loading, setLoading] = useState(false);
    const [showAppointmentHistoryDrawer, setShowAppointmentHistoryDrawer] = useState(false);
    const [recentlyBookedRefreshKey, setRecentlyBookedRefreshKey] = useState(0);
    const [showOrderDetail, setShowOrderDetail] = useState<{ orderId: number; bookingId: number } | null>(null);

    const address = trainingState?.address ?? '';
    const addressOverride: SessionAddressOverride | undefined = trainingState?.service?.inHomeService
        ? trainingState?.addressOverride
        : undefined;
    const hasDiscount = (trainingState?.discount?.value ?? 0) > 0;
    const savedCustomPrice = useSavedCustomPriceChoice({
        petId,
        serviceId: trainingState?.service?.serviceId,
        catalogPrice: Number(trainingState?.service?.price) || 0,
        currentPrice: Number(trainingState?.price) || 0,
        useSavedCustomPrice: trainingState?.useSavedCustomPrice
    });

    useEffect(() => {
        if (Number(client?.id) !== Number(clientId)) {
            dispatch(getClientThunk(apiClientWrapper, clientId));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps -- apiClientWrapper inestable por diseño
    }, [clientId, client?.id, dispatch]);

    useEffect(() => {
        if (petId) {
            dispatch(fetchPetCustomPricesThunk(apiClientWrapper, petId));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [petId]);

    useEffect(() => {
        if (trainingState?.service?.serviceId) {
            dispatch(fetchStaffThunk(apiClientWrapper.marketplaceId));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [trainingState?.service?.serviceId, apiClientWrapper.marketplaceId]);

    useEffect(() => {
        setTraining(normalizeTrainingDates(training));
        if (training?.selectedDates?.length) {
            setSelectedDates(training.selectedDates.map(d => d.clone().tz(tz).startOf('day')));
        } else if (training?.from?.isValid()) {
            setSelectedDates([training.from.clone().tz(tz).startOf('day')]);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        setInHomeService(inHomeService);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [inHomeService]);

    useNonInitialEffect(() => {
        const serviceChanged = training?.service?.serviceId !== trainingState?.service?.serviceId;
        const fromChanged =
            (training?.from && !trainingState?.from) ||
            (!training?.from && trainingState?.from) ||
            (training?.from && trainingState?.from && !training.from.isSame(trainingState.from, 'minute'));
        const toChanged =
            (training?.to && !trainingState?.to) ||
            (!training?.to && trainingState?.to) ||
            (training?.to && trainingState?.to && !training.to.isSame(trainingState.to, 'minute'));

        if (serviceChanged || fromChanged || toChanged) {
            setTraining(normalizeTrainingDates(training));
            if (training?.selectedDates?.length) {
                setSelectedDates(training.selectedDates.map(d => d.clone().tz(tz).startOf('day')));
            } else if (training?.from?.isValid()) {
                setSelectedDates([training.from.clone().tz(tz).startOf('day')]);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [training?.service?.serviceId, training?.from, training?.to]);

    // On edit the catalog can arrive after the form mounts, and it decides whether the service
    // runs on sessions, so its booking type and location replace what the appointment carried.
    useNonInitialEffect(() => {
        const catalogService = training?.service;
        if (!catalogService || catalogService.serviceId !== trainingState?.service?.serviceId) return;

        setTraining(prev => ({
            ...prev!,
            service: {
                ...prev!.service!,
                bookingType: catalogService.bookingType,
                maxPets: catalogService.maxPets,
                inHomeService: catalogService.inHomeService
            }
        }));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [training?.service?.bookingType, training?.service?.maxPets, training?.service?.inHomeService]);

    useNonInitialEffect(() => {
        setLoading(clientsStatus === ClientsStatus.Getting);
    }, [clientsStatus]);

    useClientAddressPrefill(clientId, Boolean(trainingState?.service?.inHomeService), trainingState?.address, line =>
        setTraining(prev => ({ ...prev!, address: line }))
    );

    const {
        sessionBased,
        sessionDate,
        sessions,
        loading: sessionsLoading,
        selectedSessionId,
        sessionStart,
        multiDate,
        sessionDays,
        toggleSession,
        occurrences
    } = useSessionPicker({
        area: SERVICES_BUSINESS_AREAS.TRAINING,
        serviceId: trainingState?.service?.serviceId,
        bookingType: trainingState?.service?.bookingType,
        inHomeService: trainingState?.service?.inHomeService,
        isEdit,
        from: trainingState?.from,
        staffId: trainingState?.staffId,
        serviceSessionId: trainingState?.serviceSessionId,
        selectedDates,
        drafts: draftSessionBookings,
        appointmentId
    });

    // Several dates: each selected session is checked on its own and its alerts go under its card.
    const sessionAlertSlots = useMemo(
        () =>
            occurrences.map(occurrence => ({
                key: `${occurrence.day}|${occurrence.serviceSessionId}`,
                from: occurrence.from,
                staffId: occurrence.staffId
            })),
        [occurrences]
    );
    const sessionAlerts = useSessionAlerts({
        clientId,
        petId,
        serviceId: trainingState?.service?.serviceId,
        appointmentId,
        addressOverride,
        slots: sessionAlertSlots,
        enabled: multiDate && sessionBased
    });

    const selectSessionHandler = (session: WalkingSession) => {
        const from = sessionStart(session);

        setTraining(prev => ({
            ...prev!,
            staffId: session.staffId,
            serviceSessionId: session.id,
            from,
            to: from.clone().add(prev?.service?.duration || 60, 'minutes')
        }));
    };

    const changePeriodHandler = (from: Moment) => {
        const newTo = from.clone().add(trainingState?.service?.duration || 60, 'minutes');
        const datesForEdit = [from.clone().tz(tz).startOf('day')];
        if (isEdit) {
            setSelectedDates(datesForEdit);
        }
        setTraining(prev => {
            // The date pickers echo their props back, so only a real move off the session
            // start clears the pick.
            const startMoved = !prev?.from?.isValid() || !from.isSame(prev.from, 'minute');

            return {
                ...prev!,
                ...(startMoved ? { serviceSessionId: null } : {}),
                from: from.clone().tz(timeZone),
                to: newTo.clone().tz(timeZone)
            };
        });
    };

    const changeSelectedDatesHandler = (datesOrUpdater: SetStateAction<Array<Moment>>) => {
        const next = typeof datesOrUpdater === 'function' ? datesOrUpdater(selectedDates) : datesOrUpdater;
        const normalized = next.map(d => d.clone().tz(tz).startOf('day'));
        const datesChanged =
            normalized.length !== selectedDates.length ||
            normalized.some((date, index) => !date.isSame(selectedDates[index], 'day'));

        setSelectedDates(normalized);
        if (datesChanged) setTraining(prev => ({ ...prev!, serviceSessionId: null }));
    };

    const effectiveDurationMinutes =
        trainingState?.service?.duration && trainingState.service.duration > 0
            ? trainingState.service.duration
            : trainingState?.from?.isValid() && trainingState?.to?.isValid()
            ? Math.max(0, trainingState.to.diff(trainingState.from, 'minutes'))
            : trainingState?.service?.duration ?? 0;

    const effectiveServicePrice = trainingState?.service?.price ?? trainingState?.price;

    const allowZeroPrice = enrollment?.id != null;
    const priceInvalid =
        effectiveServicePrice == null ||
        Number.isNaN(Number(effectiveServicePrice)) ||
        (allowZeroPrice ? Number(effectiveServicePrice) < 0 : Number(effectiveServicePrice) <= 0);

    // Picked sessions carry their own staff, so the staff field only matters for a date without one.
    const everyDateHasSession =
        multiDate && sessionDays.length > 0 && sessionDays.every(entry => entry.selectedSessionIds.length > 0);

    // The typed time is only booked on a date without a picked session, so it is only checked then.
    const { routedAlerts: typedTimeAlerts, dateTimeAlerts: typedTimeDateTimeAlerts } = useBookingAlerts({
        clientId,
        petId,
        from: trainingState?.from,
        to: trainingState?.to,
        serviceId: trainingState?.service?.serviceId,
        staffId: trainingState?.staffId,
        appointmentId,
        addressOverride,
        enabled: !everyDateHasSession
    });
    // Alerts every selected session returned, and the pet and client ones, show once on the form.
    const { shared: sharedSessionAlerts, bySlot: cardAlerts } = useMemo(
        () => splitSessionAlerts(sessionAlerts),
        [sessionAlerts]
    );
    const routedAlerts = useMemo(
        () => mergeRoutedAlerts(typedTimeAlerts, sharedSessionAlerts),
        [typedTimeAlerts, sharedSessionAlerts]
    );
    const dateTimeAlerts = useMemo(
        () =>
            mergeRoutedAlerts({ ...typedTimeAlerts, dateTime: typedTimeDateTimeAlerts }, sharedSessionAlerts).dateTime,
        [typedTimeAlerts, typedTimeDateTimeAlerts, sharedSessionAlerts]
    );

    const isDisabled =
        !trainingState?.service ||
        (!trainingState?.staffId && !everyDateHasSession) ||
        (trainingState?.service?.inHomeService && !trainingState?.address?.trim()) ||
        effectiveDurationMinutes <= 0 ||
        priceInvalid ||
        (!isEdit && selectedDates.length === 0);

    const periodFrom = trainingState?.from?.isValid()
        ? trainingState.from.clone().tz(timeZone)
        : moment.tz(timeZone).seconds(0).milliseconds(0);
    const periodTo = trainingState?.to?.isValid()
        ? trainingState.to.clone().tz(timeZone)
        : periodFrom.clone().add(trainingState?.service?.duration || 60, 'minutes');

    return (
        <div className={styles.trainingContainer}>
            <div className={styles.configContainer}>
                <div className={styles.section}>
                    {isEdit ? (
                        <PeriodSelector
                            from={periodFrom}
                            to={periodTo}
                            labelDate="Date"
                            labelTimeStart="Start Time"
                            hideEndTime
                            isRecurrent={false}
                            frequencyOptions={[]}
                            alerts={dateTimeAlerts}
                            onChangeFrequencyDuration={noop}
                            onChangePeriod={changePeriodHandler}
                        />
                    ) : (
                        <MultipleDateSelector
                            from={periodFrom}
                            to={periodTo}
                            selectedDates={selectedDates}
                            labelDate="Date"
                            labelTimeStart="Start Time"
                            hideEndTime
                            isRecurrent={false}
                            frequencyOptions={[]}
                            alerts={dateTimeAlerts}
                            onChangeFrequencyDuration={noop}
                            onChangeSelectedDates={changeSelectedDatesHandler}
                            onChangePeriod={changePeriodHandler}
                        />
                    )}
                </div>
                {routedAlerts.general.length > 0 && (
                    <div className={styles.section}>
                        <BookingAlertList alerts={routedAlerts.general} />
                    </div>
                )}
                <div className={styles.section}>
                    <RecentlyBooked
                        clientId={clientId}
                        petId={petId}
                        refreshTrigger={recentlyBookedRefreshKey}
                        onViewAll={() => setShowAppointmentHistoryDrawer(true)}
                        onAppointmentClick={(appointment: AppointmentListItem) => {
                            if (appointment.orderId != null && appointment.bookingId != null) {
                                setShowOrderDetail({ orderId: appointment.orderId, bookingId: appointment.bookingId });
                            }
                        }}
                    />
                </div>
                <div className={styles.section}>
                    <TrainingServiceSelector
                        loading={servicesStatus === ServicesStatus.FetchingTrainingServices}
                        className={styles.serviceSelector}
                        value={trainingState?.service}
                        inHomeService={inHomeServiceState}
                        onChange={value => {
                            const previousServiceId = trainingState?.service?.serviceId;
                            const newServiceId = value?.serviceId;
                            const catalogPrice = value?.price || 0;
                            const sameService = previousServiceId === newServiceId;
                            const keepEditedPrice = sameService && trainingState?.price != null;
                            const resolved = resolveStandardPrice(
                                catalogPrice,
                                customPrices || [],
                                value?.serviceId || 0
                            );

                            setTraining({
                                ...trainingState!,
                                service: value!,
                                ...(sameService ? {} : { serviceSessionId: null }),
                                to:
                                    trainingState?.from?.isValid() && value?.duration
                                        ? trainingState.from.clone().add(value.duration, 'minutes')
                                        : trainingState?.to,
                                price: keepEditedPrice ? trainingState!.price : resolved.price,
                                customPriceId: sameService
                                    ? trainingState?.customPriceId ?? resolved.customPriceId
                                    : resolved.customPriceId,
                                useSavedCustomPrice: sameService ? trainingState?.useSavedCustomPrice : undefined,
                                saveCustomPrice: sameService ? trainingState?.saveCustomPrice : false
                            });

                            if (previousServiceId !== newServiceId) {
                                setInHomeService(undefined);
                            }
                        }}
                        petId={petId}
                        servicesUsed={servicesUsed}
                    />
                </div>

                {trainingState?.service && sessionBased && (
                    <div className={styles.section}>
                        <SessionSelector
                            sessions={sessions}
                            loading={sessionsLoading}
                            date={sessionDate}
                            selectedSessionId={selectedSessionId}
                            onSelect={selectSessionHandler}
                            groups={
                                multiDate
                                    ? sessionDays.map(entry => ({
                                          key: entry.day,
                                          date: entry.date,
                                          sessions: entry.sessions,
                                          loading: entry.loading,
                                          selectedSessionIds: entry.selectedSessionIds,
                                          alertsBySessionId: Object.fromEntries(
                                              entry.selectedSessionIds
                                                  .filter(id => cardAlerts[`${entry.day}|${id}`])
                                                  .map(id => [id, cardAlerts[`${entry.day}|${id}`]])
                                          )
                                      }))
                                    : undefined
                            }
                            onSelectInGroup={toggleSession}
                            serviceName={trainingState.service.serviceName}
                        />
                        <BookingAlertList alerts={routedAlerts.session} />
                        {!multiDate && routedAlerts.sessionFull.length > 0 && (
                            <SessionFullAlert serviceName={trainingState.service.serviceName} />
                        )}
                    </div>
                )}

                {trainingState?.service && trainingState?.service?.inHomeService && (
                    <div className={styles.section}>
                        <p className={styles.title}>Client Address</p>
                        {loading ? (
                            <Spinner />
                        ) : (
                            <ClientAddressAutocomplete
                                value={address}
                                className={styles.input}
                                onChange={value =>
                                    setTraining({
                                        ...trainingState!,
                                        address: value
                                    })
                                }
                                onSelectPlace={(place: SelectedPlace) =>
                                    setTraining(prev => ({
                                        ...prev!,
                                        address: place.address,
                                        addressOverride: {
                                            latitude: place.latitude,
                                            longitude: place.longitude,
                                            zipcode: place.zipcode
                                        }
                                    }))
                                }
                            />
                        )}
                        <BookingAlertList alerts={routedAlerts.zone} />
                    </div>
                )}

                {trainingState?.service && trainingState?.service?.inHomeService && (
                    <div className={styles.section}>
                        <p className={styles.title}>Instructions</p>
                        <Input
                            value={trainingState?.instructions ?? ''}
                            className={styles.input}
                            placeholder="Gate code, where to meet, where the keys are"
                            onChange={event =>
                                setTraining({
                                    ...trainingState!,
                                    instructions: event.target.value
                                })
                            }
                        />
                    </div>
                )}

                {trainingState?.service && (
                    <div className={styles.section}>
                        <div className={styles.serviceData}>
                            <DurationSelect
                                label="Duration"
                                selectedOption={trainingState?.service}
                                onSelect={value => {
                                    const nextService = value as TrainingService;
                                    const nextDuration = nextService?.duration ?? 0;
                                    const nextTo =
                                        trainingState?.from?.isValid() && nextDuration > 0
                                            ? trainingState.from.clone().add(nextDuration, 'minutes')
                                            : trainingState?.to;
                                    setTraining({
                                        ...trainingState!,
                                        service: nextService,
                                        to: nextTo
                                    });
                                }}
                                type="training"
                            />
                            <div
                                className={clsx(styles.PriceSection, {
                                    [lockStyles.interactionLock]: !canEditPrices
                                })}
                            >
                                <PriceField
                                    id="booking_service_input_price"
                                    label="Price"
                                    value={trainingState?.price || trainingState?.service?.price}
                                    onChange={value =>
                                        setTraining({
                                            ...trainingState!,
                                            price: value!
                                        })
                                    }
                                    className={styles.price}
                                    placeholder="$0.00"
                                    scale={2}
                                    precision={5}
                                />
                            </div>
                        </div>
                        {savedCustomPrice.show && (
                            <SavedCustomPriceChoice
                                petName={savedCustomPrice.petName}
                                savedPrice={savedCustomPrice.savedPrice}
                                mode={savedCustomPrice.mode}
                                disabled={!canEditPrices}
                                onChange={mode => {
                                    const next = savedCustomPrice.applyMode(mode);
                                    setTraining({
                                        ...trainingState!,
                                        price: next.price,
                                        useSavedCustomPrice: next.useSavedCustomPrice,
                                        customPriceId: next.customPriceId
                                    });
                                }}
                            />
                        )}
                        <SaveCustomPriceCheckbox
                            checked={!!trainingState?.saveCustomPrice}
                            disabled={!canEditPrices}
                            currentPrice={trainingState?.price}
                            defaultPrice={Number(trainingState?.service?.price) || 0}
                            onChange={checked =>
                                setTraining({
                                    ...trainingState!,
                                    saveCustomPrice: checked
                                })
                            }
                        />
                    </div>
                )}

                {trainingState?.service && (
                    <div
                        className={clsx(styles.section, {
                            [lockStyles.interactionLock]: !canApplyDiscount && hasDiscount,
                            [lockStyles.hiddenComponent]: !canApplyDiscount && !hasDiscount
                        })}
                    >
                        <ValueTypeSelector
                            label="Discount"
                            value={trainingState?.discount}
                            className={styles.discount}
                            max={
                                trainingState?.discount?.type === 'percentage'
                                    ? 100
                                    : trainingState?.discount?.type === 'amount'
                                    ? trainingState?.service?.price
                                    : 0.5
                            }
                            onChange={value =>
                                setTraining({
                                    ...trainingState!,
                                    discount: value!
                                })
                            }
                        />
                    </div>
                )}

                {trainingState?.service && trainingState?.service?.serviceId && (
                    <div className={styles.section}>
                        <StaffSelector
                            loading={
                                !trainingState?.service || staffStatus === StaffStatus.Fetching || staffs.length === 0
                            }
                            type="training"
                            key={trainingState?.service?.serviceId}
                            selectedStaff={trainingState?.staffId}
                            autoSelect={false}
                            service={trainingState?.service}
                            alerts={routedAlerts.staff}
                            onSelectStaff={value =>
                                setTraining(prev => ({
                                    ...prev!,
                                    staffId: value?.id,
                                    ...(prev?.staffId !== value?.id ? { serviceSessionId: null } : {})
                                }))
                            }
                        />
                    </div>
                )}
            </div>
            <div className={styles.footerContainer}>
                <Button
                    isLoading={
                        newBookingStatus === NewBookingsStatus.SavingCreateAppointment ||
                        newBookingStatus === NewBookingsStatus.SingleRescheduling ||
                        ordersStatus === OrdersStatus.Reloading
                    }
                    className={styles.buttonSave}
                    disabled={isDisabled}
                    variant="green"
                    label="Save"
                    size="large"
                    onClick={() => {
                        const selectedSession = sessions.find(session => session.id === selectedSessionId);
                        // Send the session the form shows as selected, never a stale stored link.
                        // Its start pins `from` to the session's day so the slot keeps the link.
                        const sessionFrom = selectedSession ? sessionStart(selectedSession) : undefined;
                        const normalizedTraining = normalizeTrainingDates({
                            ...trainingState,
                            ...(sessionFrom
                                ? {
                                      from: sessionFrom,
                                      to: trainingState.to?.isValid()
                                          ? sessionFrom
                                                .clone()
                                                .add(trainingState.to.diff(trainingState.from, 'minutes'), 'minutes')
                                          : trainingState.to
                                  }
                                : {}),
                            serviceSessionId: selectedSession?.id ?? null
                        });
                        const resolvedSelectedDates = isEdit
                            ? [normalizedTraining.from.clone().tz(tz).startOf('day')]
                            : selectedDates.map(d => d.clone().tz(tz).startOf('day'));
                        const payload: Training = {
                            ...normalizedTraining,
                            selectedDates: resolvedSelectedDates,
                            sessionSlots: multiDate && sessionBased ? occurrences : undefined
                        };
                        if (enrollment != null) {
                            payload.enrollment = enrollment;
                        } else {
                            delete (payload as { enrollment?: unknown }).enrollment;
                        }
                        if (membershipItem != null) {
                            payload.membershipItem = membershipItem;
                        } else {
                            delete (payload as { membershipItem?: unknown }).membershipItem;
                        }
                        onNext(payload);
                    }}
                />
            </div>
            <AppointmentHistoryDrawer
                open={showAppointmentHistoryDrawer}
                onBack={() => {
                    setShowAppointmentHistoryDrawer(false);
                    setRecentlyBookedRefreshKey(k => k + 1);
                }}
                customerId={clientId}
                petId={petId}
                onAppointmentClick={(appointment: AppointmentListItem) => {
                    if (appointment.orderId != null && appointment.bookingId != null) {
                        setShowOrderDetail({ orderId: appointment.orderId, bookingId: appointment.bookingId });
                    }
                }}
            />
            {showOrderDetail && (
                <OrderDetail
                    orderId={showOrderDetail.orderId}
                    bookingId={showOrderDetail.bookingId}
                    readOnly={true}
                    onClose={() => setShowOrderDetail(null)}
                    zIndex={10100}
                />
            )}
        </div>
    );
};

export default TrainingComponent;
